import { ApiOnboardingStep } from "@incendium/api";

export type OnboardingStepMeta = {
  step: string;
  title: string;
  titleAccent?: string;
  description?: string;
  descriptionParagraphs?: string[];
};

export const onboardingStepsMap: Record<ApiOnboardingStep, OnboardingStepMeta> = {
  [ApiOnboardingStep.BASIC_INFO]: {
    step: "Basic Info",
    title: "Your Account Info",
    description: "Confirm your timezone & default currency. These will be used in your analytics reports.",
  },
  [ApiOnboardingStep.BUSINESS_INFO]: {
    step: "Website Scan",
    title: "Scanning your Site",
    description: "Incendium AI has generated a top level picture of your brand which will be used by our AI Agents, for example when they build you a landing page or answer questions. Make any tweaks you think are important.",
  },
  [ApiOnboardingStep.PLATFORM]: {
    step: "Platform",
    title: "Confirm your Platform",
    description: "We identified you're using shopify, click next to confirm",
  },
  [ApiOnboardingStep.PIXEL]: {
    step: "Pixel",
    title: "Installing Tracking Pixel",
    description:
      "Our pixel is lightweight but powerful. Expect to capture 10-20% more sessions, and 20%+ more returning users than your current analytics!",
  },
  [ApiOnboardingStep.LOCATIONS]: {
    step: "Locations",
    title: "Setting Up Page Locations (Groupings)",
    description: "Locations turn your pages into meaningful groups: product pages, categories, blogs, checkout flows. It's how Incendium learns your structure so it can map journeys, detect drop-offs, and surface insights fast.",
  },
  [ApiOnboardingStep.CONNECTIONS]: {
    step: "Connections",
    title: "Connect your ad platforms",
    description:
      "Connect your ad accounts so Incendium can match spend, clicks and campaign data with your store performance. This powers clearer attribution, cleaner reporting and faster insights.",
  },
};

export const STANDARD_ONBOARDING_STEP_ORDER: ApiOnboardingStep[] = [
  ApiOnboardingStep.BASIC_INFO,
  ApiOnboardingStep.BUSINESS_INFO,
  ApiOnboardingStep.PLATFORM,
  ApiOnboardingStep.PIXEL,
  ApiOnboardingStep.LOCATIONS,
  ApiOnboardingStep.CONNECTIONS,
];

export const STANDARD_ONBOARDING_TOTAL_STEPS =
  STANDARD_ONBOARDING_STEP_ORDER.length;

export const standardOnboardingStepNumber: Partial<
  Record<ApiOnboardingStep, number>
> = Object.fromEntries(
  STANDARD_ONBOARDING_STEP_ORDER.map((step, index) => [step, index + 1]),
) as Partial<Record<ApiOnboardingStep, number>>;

const orderedSteps = STANDARD_ONBOARDING_STEP_ORDER;

export function getStandardNextOnboardingStep(
  step: ApiOnboardingStep,
): ApiOnboardingStep | null {
  const index = orderedSteps.indexOf(step);
  if (index < 0 || index >= orderedSteps.length - 1) {
    return null;
  }
  return orderedSteps[index + 1];
}

export function getStandardPrevOnboardingStep(
  step: ApiOnboardingStep,
): ApiOnboardingStep | null {
  const index = orderedSteps.indexOf(step);
  if (index <= 0) {
    return null;
  }
  return orderedSteps[index - 1];
}

export const isStepBefore = (
  step1: ApiOnboardingStep,
  step2: ApiOnboardingStep
): boolean => {
  return orderedSteps.indexOf(step1) < orderedSteps.indexOf(step2);
};

export function getOnboardingPanelParagraphs(
  meta: OnboardingStepMeta | undefined,
): string[] {
  if (!meta) {
    return [];
  }
  if (meta.descriptionParagraphs?.length) {
    return meta.descriptionParagraphs;
  }
  if (meta.description) {
    return [meta.description];
  }
  return [];
}
